import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBackIosNew,
  ArrowForwardIos,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const float1 = keyframes`
  0%,100% { transform: translate(0, 0) rotate(0deg); }
  25%     { transform: translate(8px, -18px) rotate(2deg); }
  50%     { transform: translate(-4px, -28px) rotate(-1deg); }
  75%     { transform: translate(-10px, -12px) rotate(1deg); }
`;
const float2 = keyframes`
  0%,100% { transform: translate(0, 0) rotate(0deg); }
  33%     { transform: translate(-12px, -16px) rotate(-2deg); }
  66%     { transform: translate(6px, -24px) rotate(1.5deg); }
`;
const progressAnim = keyframes`
  0%   { width: 0%; }
  100% { width: 100%; }
`;

const C = {
  b50: '#eff6ff', b100: '#dbeafe', b200: '#bfdbfe',
  b500: '#3b82f6', b600: '#2563eb', b700: '#1d4ed8', b900: '#1e3a5f',
  t400: '#2dd4bf', t500: '#14b8a6', t600: '#0d9488',
  grad: 'linear-gradient(135deg, #2563eb 0%, #0891b2 50%, #0d9488 100%)',
  gradSoft: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 50%, #14b8a6 100%)',
  gradBg: 'linear-gradient(165deg, #eff6ff 0%, #ecfeff 20%, #f0fdfa 40%, #ffffff 70%)',
  glow: 'rgba(37,99,235,',
  glowT: 'rgba(13,148,136,',
};

const HeroBadge = styled(motion.div)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 22px',
  borderRadius: 50,
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(37,99,235,0.12)',
  boxShadow: '0 2px 16px rgba(37,99,235,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
  marginBottom: 28,
});

const GradientBtn = styled(Button)({
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: 14,
  padding: '15px 36px',
  color: '#fff',
  background: C.grad,
  backgroundSize: '200% auto',
  boxShadow: `0 8px 32px ${C.glow}0.25), 0 2px 8px ${C.glowT}0.15)`,
  transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, #1d4ed8 0%, #0e7490 50%, #0d9488 100%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: -1,
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `0 16px 48px ${C.glow}0.3), 0 4px 12px ${C.glowT}0.2)`,
    backgroundPosition: 'right center',
    '&::after': { opacity: 1 },
  },
});

const NavArrow = styled(IconButton)({
  width: 52,
  height: 52,
  background: 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(37,99,235,0.1)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  color: '#64748b',
  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': {
    background: '#fff',
    boxShadow: `0 8px 32px ${C.glow}0.15)`,
    color: C.b600,
    transform: 'scale(1.08)',
  },
});

const Dot = styled(Box)(({ active }) => ({
  width: active ? 40 : 10,
  height: 10,
  borderRadius: 20,
  background: active ? C.grad : '#e2e8f0',
  cursor: 'pointer',
  transition: 'all 0.45s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': { background: active ? C.grad : '#94a3b8' },
}));

/* ─── Browser Mockup Frame ─── */
const BrowserFrame = styled(Box)({
  borderRadius: 20,
  overflow: 'hidden',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 30px 90px rgba(37,99,235,0.12), 0 8px 32px rgba(0,0,0,0.06)',
  position: 'relative',
});

const BrowserBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 16px',
  background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
});

const BrowserDot = styled(Box)(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: color,
}));

const BrowserUrlBar = styled(Box)({
  flex: 1,
  marginLeft: 12,
  padding: '5px 14px',
  borderRadius: 8,
  background: '#fff',
  border: '1px solid #e2e8f0',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

/* ═══════════════════════════════════════════
   SLIDES DATA — AIDA MODEL
   ═══════════════════════════════════════════ */
const SLIDES = [
  {
    id: 0,
    badge: { icon: '\u{1F680}', label: "Yeni Nesil Dernek Yönetim Sistemi" },
    title: 'Resmi Altyapılı ',
    highlight: 'Dernek Yönetim Platformu',
    desc: 'Üye yönetimi, aidat takibi, bağış sistemi, resmi yazışma ve web sitesi yönetimini tek platformda yönetin.',
    features: ['E-İmza destekli', 'Resmi yazışma altyapısı', 'Resmi yazışma altyapısı'],
    cta1: { text: 'Hemen Başlayın', to: '/basvuru' },
    cta2: { text: 'Demo İzleyin', to: '/demo' },
    img: '/assets/page_images/hero_slide1.webp',
    url: 'app.superdernek.com/dashboard',
    floatTop: { dot: '#22c55e', dotGlow: 'rgba(34,197,94,0.5)', label: 'Canlı Sistem' },
    floatBottom: { icon: '\u{1F310}', title: '1.200+ Dernek', sub: 'Aktif platform' },
    accent: '#03c0ff', accentSoft: 'rgba(3,192,255,0.12)',
  },
  {
    id: 1,
    badge: { icon: '🧩', label: 'Widget Tabanlı Sistem' },
    title: 'Web Sitenizi ',
    highlight: 'Özgürce Tasarlayın',
    desc: 'Tasarımı ekranda görerek yapın, sürprizlerle karşılaşmayın. Web sitenizi widget’lar ile şekillendirin, yönetim panelinde düzeni koruyun.',
    features: [
      'Web sitesi için esnek sayfa tasarımı',
      'Dashboard widget özelleştirme',
      'Mobil uyumlu yapı'
    ],
    cta1: { text: 'Widget Sistemini İncele', to: '/solutions' },
    cta2: { text: "Widget'ları Gör", to: '/solutions' },
    img: '/assets/page_images/hero_slide2.webp',
    url: 'app.superdernek.com/dashboard',
    floatTop: { dot: '#3b82f6', dotGlow: 'rgba(59,130,246,0.5)', label: '50.000+ Üye' },
    floatBottom: { icon: '\u{1F4CA}', title: '%98 Memnuniyet', sub: 'Kullanıcı skoru' },
    accent: '#0187b3', accentSoft: 'rgba(1,135,179,0.1)',
  },
  {
    id: 2,
    badge: { icon: '\u{1F465}', label: 'Akıllı Üye Yönetimi' },
    title: 'Dernek Operasyonlarını ',
    highlight: 'Tek Panelde Yönetin',
    desc: 'Dernek yönetiminin tüm operasyonel yükünü tek panelde toplayın. Karmaşayı değil, kontrolü yönetin.',
    features: ['CRM & üye takibi', 'Toplu iletişim', 'Detaylı raporlama'],
    cta1: { text: 'Demo Talep Edin', to: '/basvuru' },
    cta2: { text: 'Özellikleri Keşfet', to: '/solutions' },
    img: '/assets/page_images/hero_slide3.webp',
    url: 'app.superdernek.com/members',
    floatTop: { dot: '#3b82f6', dotGlow: 'rgba(59,130,246,0.5)', label: '50.000+ Üye' },
    floatBottom: { icon: '\u{1F4CA}', title: '%98 Memnuniyet', sub: 'Kullanıcı skoru' },
    accent: '#0187b3', accentSoft: 'rgba(1,135,179,0.1)',
  },
  {
    id: 3,
    badge: { icon: '\u{1F4B0}', label:  'Aidat & Bağış Yönetimi' },
    title: 'Tahsilatlarınızı ',
    highlight: 'Kolayca Yönetin',
    desc: 'Aidat ve bağış süreçlerini manuel takibin dışına çıkarın. Ödemeler düzenli, kayıtlar net olsun.',
    features: ['Online bağış sistemi', 'Otomatik hatırlatma', 'Ödeme entegrasyonu'],
    cta1: { text: 'Ücretsiz Başlayın', to: '/basvuru' },
    cta2: { text: 'Fiyatları İncele', to: '/ucretlendirme' },
    img: '/assets/page_images/hero_slide4.webp',
    url: 'app.superdernek.com/finance',
    floatTop: { dot: '#f59e0b', dotGlow: 'rgba(245,158,11,0.5)', label: 'Gelir +%40' },
    floatBottom: { icon: '\u{1F514}', title: 'Otomatik Takip', sub: 'Aidat hatırlatma' },
    accent: '#fc8d05', accentSoft: 'rgba(252,141,5,0.12)',
  },
];

const STATS = [
  { val: '1.200+', lbl: 'Aktif Dernek' },
  { val: '50.000+', lbl: 'Kayıtlı Üye' },
  { val: '%99.9', lbl: 'Uptime SLA' },
];

const AUTOPLAY = 10000;

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */
const HeroKurumsal = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cur, setCur] = useState(0);
  const [dir, setDir] = useState(1);
  const [pKey, setPKey] = useState(0);
  const touchX = useRef(0);

  const go = useCallback((i) => {
    setDir(i > cur ? 1 : -1);
    setCur(i);
    setPKey((k) => k + 1);
  }, [cur]);

  const next = useCallback(() => {
    setDir(1);
    setCur((p) => (p + 1) % SLIDES.length);
    setPKey((k) => k + 1);
  }, []);

  const prev = useCallback(() => {
    setDir(-1);
    setCur((p) => (p - 1 + SLIDES.length) % SLIDES.length);
    setPKey((k) => k + 1);
  }, []);

  /* touch */
  const onTouchStart = (e) => { touchX.current = e.changedTouches[0].screenX; };
  const onTouchEnd = (e) => {
    const d = touchX.current - e.changedTouches[0].screenX;
    if (Math.abs(d) > 50) d > 0 ? next() : prev();
  };

  /* keyboard */
  useEffect(() => {
    const h = (e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev]);

  /* autoplay */
  // useEffect(() => {
  //   const t = setInterval(next, AUTOPLAY);
  //   return () => clearInterval(t);
  // }, [next]);

  const s = SLIDES[cur];

  /* variants */
  const txtV = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };
  const imgV = {
    enter: (d) => ({ x: d > 0 ? 90 : -90, opacity: 0, scale: 0.94 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (d) => ({ x: d > 0 ? -90 : 90, opacity: 0, scale: 0.94 }),
  };

  return (
    <Box
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '50vh' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
       //background: C.gradBg,
      }}
    >
      {/* ── Decorative blobs ── */}
      <Box sx={{
        position: 'absolute', width: 500, height: 500, top: -180, left: -100,
        background: `radial-gradient(circle, ${C.glow}0.07) 0%, transparent 70%)`,
        filter: 'blur(80px)', pointerEvents: 'none',
        animation: `${float1} 20s ease-in-out infinite`,
      }} />
      <Box sx={{
        position: 'absolute', width: 400, height: 400, bottom: -100, right: -80,
        background: `radial-gradient(circle, ${C.glowT}0.06) 0%, transparent 70%)`,
        filter: 'blur(80px)', pointerEvents: 'none',
        animation: `${float2} 18s ease-in-out 3s infinite`,
      }} />
      <Box sx={{
        position: 'absolute', width: 300, height: 300, top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        background: `radial-gradient(circle, ${C.glow}0.04) 0%, transparent 70%)`,
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      {/* ── Grid dots ── */}
      {/* <Box sx={{
        position: 'absolute', inset: 0, opacity: 0.35,
        backgroundImage: 'radial-gradient(#94a3b8 0.8px, transparent 0.8px)',
        backgroundSize: '28px 28px', pointerEvents: 'none',
      }} /> */}

      {/* ── Arrows ── */}
      {!isMobile && (
        <>
          <Box sx={{ position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <NavArrow onClick={prev}><ArrowBackIosNew sx={{ fontSize: 18 }} /></NavArrow>
          </Box>
          <Box sx={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
            <NavArrow onClick={next}><ArrowForwardIos sx={{ fontSize: 18 }} /></NavArrow>
          </Box>
        </>
      )}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 5, md: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">

          {/* ════ LEFT ════ */}
          <Grid item xs={12} md={6}>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={s.id}
                custom={dir}
                variants={txtV}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Badge */}
                <HeroBadge
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  <Typography sx={{
                    fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.3,
                    background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    {s.badge.label}
                  </Typography>
                </HeroBadge>

                {/* Title */}
                <Typography variant="h1" sx={{
                  fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
                  fontWeight: 900, lineHeight: 1.08, color: '#0f172a',
                  letterSpacing: '-0.02em', mb: 2.5, maxWidth: 620,
                }}>
                  {s.title}
                  <Box component="span" sx={{
                    background: C.grad,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline',
                  }}>
                    {s.highlight}
                  </Box>
                </Typography>

                {/* Description */}
                <Typography sx={{
                  fontSize: { xs: '1rem', md: '1.12rem' },
                  color: '#475569', lineHeight: 1.85, mb: 3, maxWidth: 520,
                }}>
                  {s.desc}
                </Typography>

                {/* Mini features list */}
                <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', mb: 4 }}>
                  {s.features.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <CheckIcon sx={{ fontSize: 16, color: C.t500 }} />
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTAs */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: { xs: 3, md: 5 } }}>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <GradientBtn endIcon={<ArrowForwardIcon />} onClick={() => navigate(s.cta1.to)}>
                      {s.cta1.text}
                    </GradientBtn>
                  </motion.div>
                </Box>

                {/* Trust stats */}
                <Box sx={{
                  display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap',
                  pt: 2.5, borderTop: '1px solid #e2e8f0',
                }}>
                  {STATS.map((st, i) => (
                    <Box key={i}>
                      <Typography sx={{
                        fontWeight: 900, fontSize: '1.15rem', color: '#0f172a',
                        background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      }}>
                        {st.val}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, letterSpacing: 0.5, mt: 0.2 }}>
                        {st.lbl}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </AnimatePresence>
          </Grid>

          {/* ════ RIGHT — Browser Mockup ════ */}
          <Grid item xs={12} md={6}>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={`img-${s.id}`}
                custom={dir}
                variants={imgV}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              >
                <Box sx={{ position: 'relative', maxWidth: 620, mx: 'auto' }}>
                  {/* Browser window */}
                  <BrowserFrame>
                    <BrowserBar>
                      <BrowserDot color="#ff5f57" />
                      <BrowserDot color="#febc2e" />
                      <BrowserDot color="#28c840" />
                      <BrowserUrlBar>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, background: C.grad, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {s.url}
                        </Typography>
                      </BrowserUrlBar>
                    </BrowserBar>
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <img
                        src={s.img}
                        alt={s.highlight}
                        loading="eager"
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                      />
                      {/* Subtle gradient overlay at bottom */}
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%',
                        background: 'linear-gradient(transparent, rgba(255,255,255,0.4))',
                        pointerEvents: 'none',
                      }} />
                    </Box>
                  </BrowserFrame>
                </Box>
              </motion.div>
            </AnimatePresence>
          </Grid>
        </Grid>

        {/* ── Dots + Counter ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            {SLIDES.map((_, i) => (
              <Dot key={i} active={i === cur} onClick={() => go(i)} />
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, letterSpacing: 2, fontFamily: 'monospace' }}>
            {String(cur + 1).padStart(2, '0')}&nbsp;/&nbsp;{String(SLIDES.length).padStart(2, '0')}
          </Typography>
        </Box>
      </Container>

      {/* ── Progress bar ── */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3,
        background: '#e2e8f0',
      }}>
        <Box
          key={pKey}
          sx={{
            height: '100%',
            background: C.grad,
            animation: `${progressAnim} ${AUTOPLAY}ms linear forwards`,
            borderRadius: 2,
          }}
        />
      </Box>
    </Box>
  );
};

export default HeroKurumsal;
